import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBars, FaSignOutAlt, FaUserPlus, FaBox, FaBoxes, FaUsers, FaCalendarAlt, FaClock,
  FaBan, FaPlaneDeparture, FaFileInvoiceDollar, FaChartPie, FaBuilding, FaMoneyBillWave, FaExchangeAlt, FaHome
} from 'react-icons/fa';
import { FaGift } from 'react-icons/fa';
import axios from 'axios';
import { API_CONFIG } from '../../../utils/apiConfig';
import './SuperAdminSidebar.css';
import { evaluate } from 'mathjs';
import { useAuth } from '../../../contexts/AuthContext';

const SuperAdminSidebar = ({ isOpen, toggleSidebar, handleLogout, user, setUser }) => {
  const [calcInput, setCalcInput] = useState('');
  const [calcResult, setCalcResult] = useState('');
  const [profilePic, setProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const API_BASE_URL = API_CONFIG.BASE_URL;
  const { currentUser } = useAuth();

  useEffect(() => {
    if (user && user.profilePic) {
      if (user.profilePic.startsWith('data:image/')) {
        setPreviewUrl(user.profilePic);
      } else if (user.profilePic.startsWith('http')) {
        const originalUrl = user.profilePic;
        const cacheBustUrl = originalUrl + (originalUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
        setPreviewUrl(cacheBustUrl);
      } else {
        setPreviewUrl(user.profilePic);
      }
    } else {
      setPreviewUrl(null);
    }
  }, [user, setUser]);


  const handleCalcButtonClick = (value) => {
    if (value === '=') {
      try {
        setCalcResult(evaluate(calcInput).toString());
      } catch {
        setCalcResult('Error');
      }
    } else if (value === 'C') {
      setCalcInput('');
      setCalcResult('');
    } else {
      setCalcInput(calcInput + value);
    }
  };

  const handleForgotPassword = () => {
    try {
      const response = axios.post(`${API_BASE_URL}/api/superadmin/forgot-password`, {
        email: user?.email
      });

      if (response.data.success) {
        setUploadMessage({ type: 'success', text: 'Password reset link sent to your email!' });
        setTimeout(() => setUploadMessage(null), 5000);
      }
    } catch (error) {
      setUploadMessage({ type: 'error', text: error.response?.data?.message || 'Failed to send reset link' });
      setTimeout(() => setUploadMessage(null), 5000);
    }
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadMessage({ type: 'error', text: 'Please select a valid image file.' });
        setTimeout(() => setUploadMessage(null), 3000);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setUploadMessage({ type: 'error', text: 'Image size must be less than 5MB.' });
        setTimeout(() => setUploadMessage(null), 3000);
        return;
      }
      
      setProfilePic(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadMessage(null);
    }
  };

  const handleSaveProfilePic = async (isEdit = false) => {
    if (!profilePic) {
      setUploadMessage({ type: 'error', text: 'No image selected to upload.' });
      setTimeout(() => setUploadMessage(null), 3000);
      return;
    }

    if (!currentUser) {
      setUploadMessage({ type: 'error', text: 'User not authenticated. Please log in again.' });
      setTimeout(() => setUploadMessage(null), 3000);
      return;
    }

    try {
      setIsUploading(true);
      setUploadMessage({ type: 'info', text: 'Uploading profile picture...' });

      const formData = new FormData();
      formData.append('profilePic', profilePic, profilePic.name);

      const idToken = await currentUser.getIdToken(true);
      const endpoint = '/api/upload/profile';


      const response = await axios.post(`${API_BASE_URL}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout
      });

      if (response.data && response.data.profilePicUrl) {
        const { profilePicUrl } = response.data;
        
        const updatedUser = { ...user, profilePic: profilePicUrl };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        setPreviewUrl(profilePicUrl);
        
        setProfilePic(null);
        
        const fileInput = document.getElementById('profilePicInput');
        if (fileInput) {
          fileInput.value = '';
        }
        
        setUploadMessage({ type: 'success', text: 'Profile picture updated successfully!' });
        setTimeout(() => setUploadMessage(null), 3000);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      
      let errorMessage = 'Failed to upload profile picture';
      
      if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.message || err.response.data?.error || `Server error: ${err.response.status}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.code === 'ECONNABORTED') {
        // Timeout error
        errorMessage = 'Upload timeout. Please try again.';
      } else {
        // Other errors
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      setUploadMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setUploadMessage(null), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteProfilePic = async () => {
    if (!currentUser) {
      setUploadMessage({ type: 'error', text: 'User not authenticated. Please log in again.' });
      setTimeout(() => setUploadMessage(null), 3000);
      return;
    }

    try {
      setIsUploading(true);
      setUploadMessage({ type: 'info', text: 'Deleting profile picture...' });

      const idToken = await currentUser.getIdToken(true);
      const response = await axios.delete(`${API_BASE_URL}/api/upload/profile`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        timeout: 10000,
      });

      const updatedUser = { ...user, profilePic: null };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setPreviewUrl(null);
      
      setUploadMessage({ type: 'success', text: 'Profile picture deleted successfully!' });
      setTimeout(() => setUploadMessage(null), 3000);
    } catch (error) {
      let errorMessage = 'Failed to delete profile picture';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      setUploadMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setUploadMessage(null), 5000);
    } finally {
      setIsUploading(false);
    }
  };


  const today = new Date();
  const monthYearLabel = today.toLocaleString('default', { month: 'long', year: 'numeric' });

  const navItems = [
    { to: '/super-admin/dashboard', label: 'Dashboard', icon: <FaHome /> },
    { to: '/super-admin/create-department', label: 'Create Department', icon: <FaBuilding /> },
    { to: '/super-admin/add-admin', label: 'Add Admin', icon: <FaUserPlus /> },
    { to: '/super-admin/products', label: 'Products', icon: <FaBox /> },
    { to: '/super-admin/price-lists', label: 'Price Lists', icon: <FaBoxes /> },
    { to: '/super-admin/inventory-list', label: 'Products Inventory', icon: <FaBoxes /> },
    { to: '/super-admin/sold-products', label: 'Sold Products', icon: <FaBoxes /> },
    { to: '/super-admin/staff-list', label: 'Staff', icon: <FaUsers /> },
    { to: '/super-admin/appointments-list', label: 'Appointments', icon: <FaCalendarAlt /> },
    { to: '/super-admin/punching-times', label: 'Punching Times', icon: <FaClock /> },
    { to: '/super-admin/terminated-staff', label: 'Terminated Staff', icon: <FaBan /> },
    { to: '/super-admin/leave-requests', label: 'Leave Requests', icon: <FaPlaneDeparture /> },
    { to: '/super-admin/financial-page', label: 'Financials', icon: <FaFileInvoiceDollar /> },
    { to: '/super-admin/profit-analysis', label: 'Profit Analysis', icon: <FaChartPie /> },
    { to: '/super-admin/vouchers', label: 'Vouchers', icon: <FaGift /> },
    { to: '/super-admin/add-bank-details', label: 'Add Bank Details', icon: <FaMoneyBillWave /> },
    { to: '/super-admin/add-company', label: 'Manage Companies', icon: <FaBuilding /> },
    { to: '/super-admin/change-requests', label: 'Change Requests', icon: <FaExchangeAlt /> }
  ];

  return (
    <>
      {!isOpen && (
        <div className="sidebar-toggle-button" style={{ display: 'block', position: 'fixed', top: '10px', left: '10px', zIndex: 1000 }}>
          <button type="button" onClick={toggleSidebar} className="btn btn-toggle-icon" aria-label="Open Sidebar">
            <FaBars />
          </button>
        </div>
      )}

      <div className={`offcanvas-column ${isOpen ? 'show' : ''}`}>
        <div className="offcanvas-header">
          <h5>Super Admin Sidebar</h5>
          <button type="button" onClick={toggleSidebar} className="btn btn-toggle-icon" aria-label="Close Sidebar">
            <FaBars />
          </button>
        </div>

        <div className="offcanvas-body">
          <div className="profile-section-left">
            <div className="profile-avatarr">
              <label htmlFor="profilePicInput">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Super Admin Avatar"
                    className="clickable-avatarr"
                    title="Click to change profile picture"
                    onError={async (e) => {
                      console.error('Profile picture failed to load:', previewUrl);
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                      
                      // If this is a localhost URL that failed to load, clear it from user data
                      if (previewUrl && previewUrl.includes('localhost:5000/uploads/profile-pics/')) {
                        console.log('Clearing invalid localhost profile picture URL');
                        try {
                          if (currentUser && user && user.profilePic === previewUrl) {
                            const idToken = await currentUser.getIdToken(true);
                            await axios.put(`${API_BASE_URL}/api/upload/profile/clear-invalid`, {}, {
                              headers: {
                                'Authorization': `Bearer ${idToken}`,
                                'Content-Type': 'application/json'
                              }
                            });
                            
                            // Update local state
                            const updatedUser = { ...user, profilePic: null };
                            setUser(updatedUser);
                            localStorage.setItem('user', JSON.stringify(updatedUser));
                            setPreviewUrl(null);
                            console.log('Invalid profile picture cleared from user data');
                          }
                        } catch (error) {
                          console.error('Failed to clear invalid profile picture:', error);
                        }
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="avatarr" 
                  style={{ display: previewUrl ? 'none' : 'flex' }}
                >
                  {user?.name?.charAt(0) || 'S'}
                </div>
              </label>
              <input
                id="profilePicInput"
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                style={{ display: 'none' }}
              />
            </div>
            <div className="profile-info">
              <h4>{user?.name || 'Super Admin'}</h4>
              <p>{user?.email || 'super.admin@example.com'}</p>
              <div className="profile-pic-buttons">
                {profilePic && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSaveProfilePic(user?.profilePic !== null)}
                      className="btn btn-primary save-profile-btn"
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Uploading...
                        </>
                      ) : (
                        user?.profilePic ? 'Update' : 'Save'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setProfilePic(null);
                        setPreviewUrl(user?.profilePic || null);
                      }}
                      className="btn btn-secondary cancel-profile-btn"
                      disabled={isUploading}
                    >
                      Cancel
                    </button>
                  </>
                )}
                {user?.profilePic && !profilePic && (
                  <button
                    type="button"
                    onClick={handleDeleteProfilePic}
                    className="btn btn-danger"
                    disabled={isUploading}
                    style={{ fontSize: '12px', padding: '4px 8px', marginTop: '5px' }}
                  >
                    {isUploading ? (
                      <>
                        <i className="fa fa-spinner fa-spin"></i> Deleting...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-trash"></i> Delete
                      </>
                    )}
                  </button>
                )}
              </div>
              {uploadMessage && (
                <div className={`upload-message ${uploadMessage.type}`}>
                  {uploadMessage.type === 'info' && <i className="fa fa-info-circle"></i>}
                  {uploadMessage.type === 'success' && <i className="fa fa-check-circle"></i>}
                  {uploadMessage.type === 'error' && <i className="fa fa-exclamation-circle"></i>}
                  {uploadMessage.text}
                </div>
              )}
            </div>
          </div>

          <div className="sidebar-nav-column">
            {navItems.map((item, index) => (
              <button
                key={index}
                type="button"
                className="sidebar-btn"
                onClick={() => {
                  try {
                    toggleSidebar();
                    if (item.to) {
                      navigate(item.to);
                    } else if (item.onClick) {
                      item.onClick();
                    }
                  } catch (error) {
                    console.error('Navigation error:', error);
                  }
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>

          <h3>Calendar</h3>
          <div className="calendar">
            <div className="calendar-header">
              <button type="button">&lt;</button>
              <span>{monthYearLabel}</span>
              <button type="button">&gt;</button>
            </div>
            <div className="calendar-days">
              <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
              {[...Array(26)].map((_, i) => <span key={i} className="empty-day"></span>)}
              {[...Array(31)].map((_, i) => (
                <span key={i + 26} className={i + 1 === new Date().getDate() ? 'current-day' : ''}>{i + 1}</span>
              ))}
            </div>
          </div>

          <h3>Mini Calculator</h3>
          <div className="mini-calculator">
            <input
              type="text"
              value={calcInput}
              onChange={(e) => setCalcInput(e.target.value)}
              placeholder="Enter calculation"
            />
            <div className="calc-result">{calcResult}</div>
            <div className="calc-buttons">
              {['7', '8', '9', '/', '4', '5', '6', '*', '1', '2', '3', '-', '0', '.', '=', '+', 'C'].map((btn) => (
                <button key={btn} type="button" onClick={() => handleCalcButtonClick(btn)}>{btn}</button>
              ))}
            </div>
          </div>

          <h3>Forgot Password</h3>
          <button type="button" className="forgot-passwrd-btn" onClick={handleForgotPassword}>
            Reset Password for All
          </button>

          <button type="button" className="sidebar-btn logout-btn" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default SuperAdminSidebar;